import React from 'react';
import { Patient } from '../../types/api';
import { AlertModalState } from '../../types/dispense';
import { apiService } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import { getTodayISO } from '../../utils/date';

interface MCModalProps {
  show: boolean;
  editingMC: any | null;
  mcFromDate: string;
  mcDays: number;
  mcDateError: string;
  mcRemarks: string;
  patientName: string;
  visitULID: string;
  setShow: (show: boolean) => void;
  setEditingMC: (mc: any | null) => void;
  setMcFromDate: (date: string) => void;
  setMcDays: (days: number) => void;
  setMcDateError: (error: string) => void;
  setMcRemarks: (remarks: string) => void;
  setAlertModal: (modal: AlertModalState) => void;
  loadMCHistory: () => Promise<void>;
}

export function MCModal({
  show, editingMC, mcFromDate, mcDays, mcDateError, mcRemarks,
  patientName, visitULID,
  setShow, setEditingMC, setMcFromDate, setMcDays, setMcDateError, setMcRemarks,
  setAlertModal, loadMCHistory
}: MCModalProps) {
  if (!show) return null;

  const handleClose = () => {
    setShow(false);
    setEditingMC(null);
    setMcDateError('');
    setMcFromDate(getTodayISO());
    setMcDays(1);
    setMcRemarks('');
  };

  const handleSave = async () => {
    try {
      // Validate date is not in the past
      const today = getTodayISO();
      if (mcFromDate < today) {
        setMcDateError('Past dates are not allowed. Please select today or a future date.');
        return;
      }

      // Validate days (max 2 days for tele visits)
      if (mcDays < 1 || mcDays > 2) {
        setAlertModal({
          show: true,
          message: 'Number of days must be between 1 and 2 for tele visits.',
          type: 'error'
        });
        return;
      }

      if (editingMC) {
        // Update existing MC
        const result = await apiService.updateMC(visitULID, editingMC.ulid, {
          mcULID: editingMC.ulid,
          dateStart: mcFromDate,
          days: mcDays,
          excuseDetail: editingMC.excuseDetail || '',
          remarks: mcRemarks
        });

        if (result.success) {
          setAlertModal({
            show: true,
            message: 'Medical Certificate updated successfully!',
            type: 'success'
          });
          handleClose();
          await loadMCHistory();
        } else {
          setAlertModal({
            show: true,
            message: result.message || 'Failed to update MC.',
            type: 'error'
          });
        }
      } else {
        // Create new MC via API
        const result = await apiService.createMC(visitULID, {
          visitULID: visitULID,
          dateStart: mcFromDate,
          days: mcDays,
          type: 1, // MedCertType.OutpatientSickLeave (matching CheckLine)
          excuseDetail: '',
          remarks: mcRemarks,
          isCourt: false
        });

        if (result.success) {
          setAlertModal({
            show: true,
            message: 'Medical Certificate created successfully!',
            type: 'success'
          });
          handleClose();
          await loadMCHistory();
        } else {
          setAlertModal({
            show: true,
            message: result.message || 'Failed to create MC.',
            type: 'error'
          });
        }
      }
    } catch (error: any) {
      console.error(`Error ${editingMC ? 'updating' : 'creating'} MC:`, error);
      setAlertModal({
        show: true,
        message: getApiErrorMessage(error, `Could not ${editingMC ? 'update' : 'create'} medical certificate. Please try again.`),
        type: 'error'
      });
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{editingMC ? 'Edit Medical Certificate' : 'Create Medical Certificate'}</div>
        <div className="form-group">
          <label>Patient Name</label>
          <input type="text" value={patientName} disabled />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>From Date <span style={{ color: 'red' }}>*</span></label>
            <input
              type="date"
              value={mcFromDate}
              min={getTodayISO()}
              onChange={(e) => {
                const selectedDate = e.target.value;
                const today = getTodayISO();
                if (selectedDate < today) {
                  setMcDateError('Past dates are not allowed. Please select today or a future date.');
                  setMcFromDate(today);
                } else {
                  setMcDateError('');
                  setMcFromDate(selectedDate);
                }
              }}
            />
            {mcDateError && (
              <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '4px' }}>{mcDateError}</div>
            )}
            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '4px' }}>
              {editingMC ? 'MC can only be edited for current visit' : 'MC can only be created from current visit data'}
            </div>
          </div>
          <div className="form-group">
            <label style={{ textAlign: 'center', display: 'block' }}>Number of Days</label>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{
                  minWidth: '40px', height: '40px', padding: '0',
                  fontSize: '1.25rem', fontWeight: 'bold',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                onClick={() => { if (mcDays > 1) setMcDays(mcDays - 1); }}
                disabled={mcDays <= 1}
              >
                -
              </button>
              <span style={{ minWidth: '60px', textAlign: 'center', fontSize: '1.125rem', fontWeight: '500' }}>
                {mcDays}
              </span>
              <button
                type="button"
                className="btn btn-secondary"
                style={{
                  minWidth: '40px', height: '40px', padding: '0',
                  fontSize: '1.25rem', fontWeight: 'bold',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                onClick={() => { if (mcDays < 2) setMcDays(mcDays + 1); }}
                disabled={mcDays >= 2}
              >
                +
              </button>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '4px', textAlign: 'center' }}>
              Tele visit: 1-2 days only
            </div>
          </div>
        </div>
        <div className="form-group">
          <label>Remarks</label>
          <textarea
            value={mcRemarks}
            onChange={(e) => setMcRemarks(e.target.value)}
            placeholder="Enter remarks"
            rows={3}
            style={{ width: '100%', resize: 'vertical' }}
          />
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!!mcDateError}
          >
            {editingMC ? 'Update MC' : 'Create MC'}
          </button>
        </div>
      </div>
    </div>
  );
}
